// Adapter puro dos cinco arrays mensais de `/project/getGraphData`.
// Não altera fórmulas nem dados: só organiza os pontos `{ date: "MM/YYYY", value }`
// num calendário civil contínuo dentro do filtro, separando ausência de zero e
// registrando tudo que não pôde ser exibido.

export const SERIES = [
  {
    key: "baseline",
    field: "baseline",
    label: "Linha de base",
    meaning: "Planejamento original do item no mês",
    source: "schedule",
    color: "#8C96A8",
  },
  {
    key: "predicted",
    field: "predicted",
    label: "Previsto",
    meaning: "Planejamento atualizado do item no mês",
    source: "schedule",
    color: "#245AE4",
  },
  {
    key: "measured",
    field: "measured",
    label: "Medido",
    meaning: "Execução medida no mês",
    source: "schedule",
    color: "#16A34A",
  },
  {
    key: "paid",
    field: "paidValues",
    label: "Pago",
    meaning: "Valor financeiro pelo mês de movimento",
    source: "financial",
    color: "#3ACFF7",
  },
  {
    key: "toPay",
    field: "toPayValues",
    label: "A pagar",
    meaning: "Saldo aberto na última carga, pelo mês de vencimento",
    source: "financial",
    color: "#7455FB",
  },
];

export const SERIES_KEYS = SERIES.map((series) => series.key);
export const SERIES_BY_KEY = Object.fromEntries(SERIES.map((series) => [series.key, series]));

const MONTH_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const MONTH_LONG = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const API_MONTH = /^(\d{2})\/(\d{4})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;
const pad = (value) => String(value).padStart(2, "0");

// "MM/YYYY" → "YYYY-MM" (chave ordenável). Qualquer outra forma é inválida.
export const parseApiMonth = (date) => {
  if (typeof date !== "string") return null;
  const match = API_MONTH.exec(date);
  if (!match) return null;
  const month = Number(match[1]);
  const year = Number(match[2]);
  if (month < 1 || month > 12 || year < 1) return null;
  return `${match[2]}-${match[1]}`;
};

export const isMonthKey = (key) => typeof key === "string" && MONTH_KEY.test(key);

export const monthKeyFromDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
};

export const monthKeyToDate = (key) => {
  const match = MONTH_KEY.exec(key ?? "");
  if (!match) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
};

export const monthKeyToApiMonth = (key) => {
  const match = MONTH_KEY.exec(key ?? "");
  if (!match) return null;
  return `${match[2]}/${match[1]}`;
};

export const formatMonthLabel = (key, { long = false } = {}) => {
  const match = MONTH_KEY.exec(key ?? "");
  if (!match) return "—";
  const index = Number(match[2]) - 1;
  return long ? `${MONTH_LONG[index]} de ${match[1]}` : `${MONTH_SHORT[index]}/${match[1]}`;
};

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Arredonda só na exibição; o dado bruto segue intacto no modelo.
export const formatBrl = (value) => {
  if (!Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  return brl.format(rounded === 0 ? 0 : rounded);
};

const addMonths = (key, amount) => {
  const date = monthKeyToDate(key);
  date.setUTCMonth(date.getUTCMonth() + amount);
  return monthKeyFromDate(date);
};

const monthRange = (begin, end) => {
  const keys = [];
  let cursor = begin;
  while (cursor <= end) {
    keys.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return keys;
};

const normalizeSeries = (points, seriesKey, issues) => {
  const byMonth = new Map();
  if (points === undefined || points === null) {
    return { byMonth, availability: "unavailable" };
  }
  if (!Array.isArray(points)) {
    issues.invalid.push({ seriesKey, date: null, value: null, reason: "invalid_series" });
    return { byMonth, availability: "unavailable" };
  }
  points.forEach((point) => {
    const monthKey = parseApiMonth(point?.date);
    const value = point?.value;
    if (!monthKey) {
      issues.invalid.push({ seriesKey, date: point?.date ?? null, value, reason: "invalid_date" });
      return;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      issues.invalid.push({ seriesKey, date: point.date, value, reason: "invalid_value" });
      return;
    }
    if (byMonth.has(monthKey)) {
      issues.duplicates.push({ seriesKey, monthKey, value });
      return;
    }
    byMonth.set(monthKey, value);
  });
  return { byMonth, availability: points.length === 0 ? "empty" : "available" };
};

const resolveWindow = (window, normalized) => {
  const begin = window?.beginAt ? monthKeyFromDate(window.beginAt) : null;
  const end = window?.endAt ? monthKeyFromDate(window.endAt) : null;
  if (begin && end && begin <= end) return { begin, end, source: "filter" };
  const keys = normalized.flatMap(({ byMonth }) => Array.from(byMonth.keys())).sort();
  if (keys.length === 0) return { begin: null, end: null, source: "none" };
  return { begin: keys[0], end: keys[keys.length - 1], source: "data" };
};

export const buildMonthlyProjectionModel = ({ series = {}, window } = {}) => {
  const issues = { invalid: [], outsideWindow: [], duplicates: [] };
  const normalized = SERIES.map((definition) => ({
    definition,
    ...normalizeSeries(series?.[definition.field], definition.key, issues),
  }));
  const resolvedWindow = resolveWindow(window, normalized);
  const monthKeys = resolvedWindow.begin ? monthRange(resolvedWindow.begin, resolvedWindow.end) : [];
  const inWindow = new Set(monthKeys);

  const months = monthKeys.map((key) => ({
    key,
    label: formatMonthLabel(key),
    longLabel: formatMonthLabel(key, { long: true }),
    values: Object.fromEntries(normalized.map(({ definition, byMonth }) => [
      definition.key,
      byMonth.has(key) ? { status: "value", value: byMonth.get(key) } : { status: "missing" },
    ])),
  }));

  normalized.forEach(({ definition, byMonth }) => {
    byMonth.forEach((value, monthKey) => {
      if (!inWindow.has(monthKey)) {
        issues.outsideWindow.push({ seriesKey: definition.key, monthKey, value });
      }
    });
  });

  const availability = Object.fromEntries(
    normalized.map(({ definition, availability: state }) => [definition.key, state]),
  );

  return {
    months,
    monthKeys,
    monthsByKey: Object.fromEntries(months.map((month) => [month.key, month])),
    window: resolvedWindow,
    availability,
    issues: {
      ...issues,
      summary: {
        invalid: issues.invalid.length,
        outsideWindow: issues.outsideWindow.length,
        duplicates: issues.duplicates.length,
      },
    },
    isEmpty: months.every((month) => SERIES_KEYS.every((key) => month.values[key].status !== "value")),
  };
};

// Mês em foco inicial: seleção que abriu a lateral → mês atual → último do intervalo.
export const defaultFocusMonth = ({ monthKeys = [], preferred, now = new Date() } = {}) => {
  if (monthKeys.length === 0) return null;
  const preferredKey = isMonthKey(preferred) ? preferred : parseApiMonth(preferred);
  if (preferredKey && monthKeys.includes(preferredKey)) return preferredKey;
  const current = monthKeyFromDate(now);
  if (current && monthKeys.includes(current)) return current;
  return monthKeys[monthKeys.length - 1];
};

export const getCell = (model, monthKey, seriesKey) => (
  model?.monthsByKey?.[monthKey]?.values?.[seriesKey] ?? { status: "missing" }
);
